// Define the UB04 service type
export interface Ub04Service {
  id?: string;
  revenueCode: string;
  hcpcsCode: string;
  serviceDate: Date | null;
  serviceUnits: number;
  totalCharges: number;
  nonCovered: number;
  isDirty?: boolean;
}

// Define the UB04 form type
export interface Ub04Form {
  id?: string;
  policyNumber: string;
  patientControlNumber: string;
  patientName: string;
  patientDob: Date | null;
  patientSex: string;
  billingProviderName: string;
  billingProviderAddress: string;
  billingProviderCity: string;
  billingProviderState: string;
  billingProviderZip: string;
  payerName: string;
  typeOfBill: string;
  admissionDate: Date | null;
  admissionType: string;
  admissionSource: string;
  serviceStartDate: Date | null;
  serviceEndDate: Date | null;
  principalDiagnosisCode: string;
  services: Ub04Service[];
  fileUrl?: string;
  isDirty?: boolean;
}

// Create an empty UB04 service
export const createEmptyUB04Service = (): Ub04Service => {
  return {
    revenueCode: '',
    hcpcsCode: '',
    serviceDate: null,
    serviceUnits: 0,
    totalCharges: 0,
    nonCovered: 0,
    isDirty: false,
  };
};

// Create an empty UB04 form
export const createEmptyUB04Form = (policyNumber: string): Ub04Form => {
  return {
    policyNumber,
    patientControlNumber: '',
    patientName: '',
    patientDob: null,
    patientSex: '',
    billingProviderName: '',
    billingProviderAddress: '',
    billingProviderCity: '',
    billingProviderState: '',
    billingProviderZip: '',
    payerName: '',
    typeOfBill: '',
    admissionDate: null,
    admissionType: '',
    admissionSource: '',
    serviceStartDate: null,
    serviceEndDate: null,
    principalDiagnosisCode: '',
    services: [createEmptyUB04Service()],
    isDirty: false,
  };
};


// Get UB04 forms by policy number
export const getUB04FormsByPolicyNumber = async (policyNumber: string): Promise<Ub04Form[]> => {
  try {
    // This is a mock implementation. In a real application, you would make an API call.
    // const response = await axios.get(`${UB04_ENDPOINT}/policy/${policyNumber}`);
    // return response.data;
    
    // For now, return mock data
    const forms = mockGetUB04FormsByPolicyNumber(policyNumber);
    
    // Ensure dates are properly converted to Date objects
    return forms.map(form => ({
      ...form,
      patientDob: form.patientDob ? new Date(form.patientDob) : null,
      admissionDate: form.admissionDate ? new Date(form.admissionDate) : null,
      serviceStartDate: form.serviceStartDate ? new Date(form.serviceStartDate) : null,
      serviceEndDate: form.serviceEndDate ? new Date(form.serviceEndDate) : null,
      services: form.services.map(service => ({
        ...service,
        serviceDate: service.serviceDate ? new Date(service.serviceDate) : null
      }))
    }));
  } catch (error) {
    console.error('Error fetching UB04 forms:', error);
    throw error;
  }
};

// Save UB04 form
export const saveUB04Form = async (form: Ub04Form): Promise<Ub04Form> => {
  try {
    // This is a mock implementation. In a real application, you would make an API call.
    // const response = await axios.post(`${UB04_ENDPOINT}`, form);
    // return response.data;
    
    // For now, return the form with an ID
    return {
      ...form,
      id: form.id || `ub04-${Date.now()}`,
      isDirty: false,
    };
  } catch (error) {
    console.error('Error saving UB04 form:', error);
    throw error;
  }
};

// Mock implementation of getUB04FormsByPolicyNumber
const mockGetUB04FormsByPolicyNumber = (policyNumber: string): Ub04Form[] => {
  // Return empty array if policy number is not provided
  if (!policyNumber) {
    return [];
  }

  // Create a mock form with string dates to simulate API response
  // The actual Date objects will be created in getUB04FormsByPolicyNumber
  const mockForm: Ub04Form = {
    id: `ub04-${Date.now()}`,
    policyNumber,
    patientControlNumber: '12345678',
    patientName: 'John Doe',
    patientDob: new Date('1980-01-01'),
    patientSex: 'M',
    billingProviderName: 'General Hospital',
    billingProviderAddress: '123 Main St',
    billingProviderCity: 'Anytown',
    billingProviderState: 'CA',
    billingProviderZip: '12345',
    payerName: 'Blue Cross Blue Shield',
    typeOfBill: '131',
    admissionDate: new Date('2023-01-15'),
    admissionType: '1',
    admissionSource: '7',
    serviceStartDate: new Date('2023-01-15'),
    serviceEndDate: new Date('2023-01-20'),
    principalDiagnosisCode: 'J18.9',
    services: [
      {
        id: '1',
        revenueCode: '0120',
        hcpcsCode: '',
        serviceDate: new Date('2023-01-15'),
        serviceUnits: 5,
        totalCharges: 1500,
        nonCovered: 0,
      },
      {
        id: '2',
        revenueCode: '0250',
        hcpcsCode: 'J1200',
        serviceDate: new Date('2023-01-16'),
        serviceUnits: 2,
        totalCharges: 300,
        nonCovered: 0,
      },
      {
        id: '3',
        revenueCode: '0270',
        hcpcsCode: 'J0131',
        serviceDate: new Date('2023-01-17'),
        serviceUnits: 1,
        totalCharges: 200,
        nonCovered: 0,
      },
    ],
    fileUrl: 'https://example.com/sample.pdf', // Mock PDF URL
    isDirty: false,
  };

  return [mockForm];
};
